import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { Form, Field as FormikField, Formik } from 'formik';
import * as Yup from 'yup';

import {
  AdviceSearchTitle,
  SearchButton,
  FlexWrapper,
  StyledContainer,
} from './styles';

import { SEARCH_PATH, filterLabels } from '../search/constants';
import { buildSearchQuery } from '../search/helpers';
import { sendEvent } from '../../utils/analytics';

import { VisuallyHidden } from '../styled/accessibility';
import { useLayout } from '../layout/context';
import Suggestions from './suggestions';

import SearchInput from './search-input';
import { country } from '../../utils/current-site';

/**
 * The Search Box submits a form to the Shelter Search page for each site.
 * The box can be customised with suggestions which link to the search terms,
 * and restrict the search to a number of pre-defined (i.e. hard-coded) sections
 */

const SearchBox = ({
  headerText,
  placeholder,
  suggestions,
  sectionToSearch = '',
  query,
  submitCallback,
  className,
  headingElementType = 'h3',
}) => {
  const { insideContainer, insideHalfWidth } = useLayout();
  const inputRef = useRef();

  // we have to bind submitForm and setValue from within the formik render call

  return (
    <StyledContainer
      $padding={!(insideContainer || insideHalfWidth)}
      className={className}
    >
      <Formik
        enableReinitialize
        initialValues={{ query }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object({
          query: Yup.string().required('Please enter a search term'),
        })}
        onSubmit={(values) => {
          if (submitCallback) {
            submitCallback(
              buildSearchQuery({
                query: values.query,
                section: sectionToSearch,
              })
            );
            sendEvent({
              event: 'formSubmitted',
              formName: 'site-search-page',
              query: values.query,
              section: sectionToSearch,
            });
          }
        }}
      >
        {({ submitForm }) => (
          <Form
            action={SEARCH_PATH}
            method="GET"
            role="search"
            aria-label="Sitewide with filters"
            onSubmit={(e) => {
              if (
                submitCallback ||
                (!submitCallback && e.target.query.value === '')
              ) {
                // if we're on the search page already prevent a page refresh on form submission
                // OR if we're not on the search page prevent form submitting if query is empty
                e.preventDefault();
              }
              inputRef.current.blur();
              submitForm();
            }}
          >
            <fieldset>
              <VisuallyHidden as="legend">
                {`Search Shelter ${country} site by keyword with filters`}
              </VisuallyHidden>
              {headerText && (
                <AdviceSearchTitle as={headingElementType}>
                  {headerText}
                </AdviceSearchTitle>
              )}

              <FlexWrapper>
                <SearchInput
                  id="searchTerm"
                  name="query"
                  placeholder={placeholder}
                  labelText={`Enter your search keyword - ${
                    sectionToSearch
                      ? `${filterLabels[sectionToSearch]} filter selected, search results will be filtered.`
                      : 'No filter selected, all search results will be shown.'
                  }`}
                  section={sectionToSearch}
                  ref={inputRef}
                  suggestionHeading="Suggestions"
                  suggestionPrefixIcon="search"
                />
                <SearchButton
                  name="Search"
                  type="submit"
                  bg="secondary-standard"
                  icon="search"
                  iconPlacement="center"
                  aria-label="Submit Search"
                />
                <FormikField
                  type="hidden"
                  name="section"
                  value={sectionToSearch}
                />
              </FlexWrapper>
              {suggestions && (
                <Suggestions
                  suggestions={suggestions}
                  baseURL={SEARCH_PATH}
                  queryStringOptions={`section=${encodeURIComponent(
                    sectionToSearch
                  )}`}
                />
              )}
            </fieldset>
          </Form>
        )}
      </Formik>
    </StyledContainer>
  );
};

SearchBox.propTypes = {
  headerText: PropTypes.string,
  placeholder: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  sectionToSearch: PropTypes.string,
  query: PropTypes.string,
  submitCallback: PropTypes.func,
  className: PropTypes.string,
  headingElementType: PropTypes.elementType,
};

export default SearchBox;

export const SearchBoxAssembly = ({ contentfulData }) => (
  <SearchBox
    headerText={contentfulData.headerText}
    placeholder={contentfulData.placeholder}
    suggestions={contentfulData.suggestions}
    sectionToSearch={contentfulData.sectionToSearch || ''}
  />
);

SearchBoxAssembly.propTypes = {
  contentfulData: PropTypes.object,
};
